/**
 * Sales Transaction Model
 * 
 * ARCHITECTURAL PRINCIPLES:
 * - Transactions are the integration surface
 * - Invoices are customer receipts
 * - Finance consumes completed transactions
 * - SCM attaches tracking to transactions
 * 
 * NO CROSS-SYSTEM LOGIC HERE.
 */

/**
 * Transaction Status
 */
export const TransactionStatus = {
  OPEN: 'Open',
  PENDING_FULFILLMENT: 'Pending Fulfillment',
  COMPLETED: 'Completed'
};

/**
 * Transaction Event Types
 */
export const TransactionEventType = {
  CUSTOMER_PAYMENT: 'Customer Payment',
  PLATFORM_FEE: 'Platform Fee',
  SHIPPING_CHARGE: 'Shipping Charge',
  DAMAGE_INCIDENT: 'Damage Incident',
  REPLACEMENT_ITEM: 'Replacement Item',
  INSURANCE_COVERAGE: 'Insurance Coverage',
  SELLER_ADJUSTMENT: 'Seller Adjustment'
};

/**
 * Create a new Sales Transaction
 * @param {object} data - Transaction data
 * @returns {object} - Transaction object
 */
export function createTransaction(data) {
  return {
    transactionId: data.transactionId || generateTransactionId(),
    customerName: data.customerName || '',
    originPlatform: data.originPlatform || 'Direct',
    status: TransactionStatus.OPEN,
    createdDate: data.createdDate || new Date().toISOString(),
    completedDate: null,
    invoices: data.invoices || [],
    transactionHistory: data.transactionHistory || [],
    metadata: data.metadata || {}
  };
}

/**
 * Generate a transaction ID
 * Format: {PLATFORM}-{TIMESTAMP}
 */
export function generateTransactionId() {
  const timestamp = Date.now();
  return `TXN-${timestamp}`;
}

/**
 * Complete a transaction
 * - Sets status to Completed
 * - Sets completedDate
 * - Locks all invoices for editing
 * 
 * @param {object} transaction - Transaction to complete
 * @returns {object} - Updated transaction
 */
export function completeTransaction(transaction) {
  if (transaction.status === TransactionStatus.COMPLETED) {
    return transaction;
  }

  return {
    ...transaction,
    status: TransactionStatus.COMPLETED,
    completedDate: new Date().toISOString()
  };
}

/**
 * Move transaction to Pending Fulfillment
 * - Sets status to Pending Fulfillment
 * - Locks invoices for editing
 * 
 * @param {object} transaction - Transaction to mark pending
 * @returns {object} - Updated transaction
 */
export function markPendingFulfillment(transaction) {
  if (transaction.status === TransactionStatus.COMPLETED) {
    throw new Error('Cannot move completed transaction to pending fulfillment');
  }

  return {
    ...transaction,
    status: TransactionStatus.PENDING_FULFILLMENT
  };
}

/**
 * Reopen a transaction
 * - Sets status back to Open
 * - Clears completedDate
 * - Allows editing again
 * 
 * @param {object} transaction - Transaction to reopen
 * @returns {object} - Updated transaction
 */
export function reopenTransaction(transaction) {
  if (transaction.status === TransactionStatus.OPEN) {
    return transaction;
  }

  return {
    ...transaction,
    status: TransactionStatus.OPEN,
    completedDate: null
  };
}

/**
 * Check if transaction is editable
 * @param {object} transaction - Transaction to check
 * @returns {boolean} - True if editable
 */
export function isTransactionEditable(transaction) {
  return transaction.status === TransactionStatus.OPEN;
}

/**
 * Add invoice to transaction
 * @param {object} transaction - Transaction
 * @param {object} invoice - Invoice to add
 * @returns {object} - Updated transaction
 */
export function addInvoiceToTransaction(transaction, invoice) {
  if (!isTransactionEditable(transaction)) {
    throw new Error('Cannot add invoice to completed transaction');
  }

  return {
    ...transaction,
    invoices: [...transaction.invoices, invoice]
  };
}

/**
 * Update invoice in transaction
 * @param {object} transaction - Transaction
 * @param {string} invoiceId - Invoice ID to update
 * @param {object} updates - Invoice updates
 * @returns {object} - Updated transaction
 */
export function updateInvoiceInTransaction(transaction, invoiceId, updates) {
  if (!isTransactionEditable(transaction)) {
    throw new Error('Cannot edit invoice in completed transaction');
  }

  return {
    ...transaction,
    invoices: transaction.invoices.map(inv =>
      inv.invoice_id === invoiceId ? { ...inv, ...updates } : inv
    )
  };
}

/**
 * Remove invoice from transaction
 * @param {object} transaction - Transaction
 * @param {string} invoiceId - Invoice ID to remove
 * @returns {object} - Updated transaction
 */
export function removeInvoiceFromTransaction(transaction, invoiceId) {
  if (!isTransactionEditable(transaction)) {
    throw new Error('Cannot remove invoice from completed transaction');
  }

  return {
    ...transaction,
    invoices: transaction.invoices.filter(inv => inv.invoice_id !== invoiceId)
  };
}

/**
 * Get transaction summary
 * @param {object} transaction - Transaction
 * @returns {object} - Summary data
 */
export function getTransactionSummary(transaction) {
  const totalInvoices = transaction.invoices.length;
  
  // Calculate total amounts from all invoices
  let totalAmount = 0;
  transaction.invoices.forEach(invoice => {
    const lineItemsTotal = (invoice.line_items || []).reduce((sum, item) => sum + (item.amount || 0), 0);
    const feesTotal = (invoice.fees || []).reduce((sum, fee) => sum + (fee.amount || 0), 0);
    const shipping = typeof invoice.shipping === 'number' ? invoice.shipping : (invoice.shipping?.amount || 0);
    totalAmount += lineItemsTotal + feesTotal + shipping;
  });

  return {
    totalInvoices,
    totalAmount: parseFloat(totalAmount.toFixed(2)),
    status: transaction.status,
    isEditable: isTransactionEditable(transaction)
  };
}

/**
 * Add event to transaction history
 * @param {object} transaction - Transaction
 * @param {object} event - Event to add
 * @returns {object} - Updated transaction
 */
export function addTransactionEvent(transaction, event) {
  const newEvent = {
    id: `evt-${Date.now()}`,
    date: event.date || new Date().toISOString(),
    type: event.type,
    description: event.description,
    amount: event.amount || 0,
    source: event.source || '',
    metadata: event.metadata || {}
  };

  return {
    ...transaction,
    transactionHistory: [...(transaction.transactionHistory || []), newEvent]
  };
}

/**
 * Get full transaction history sorted by date
 * @param {object} transaction - Transaction
 * @returns {array} - Sorted events
 */
export function getTransactionHistory(transaction) {
  return (transaction.transactionHistory || []).sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );
}
